// =======================
// src/paginas/analise/escola/listMantUsers.js
// =======================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, CircularProgress,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Box,
  Tooltip, DialogContentText, Snackbar, Alert,
  List, ListItem, ListItemIcon, InputAdornment,
  Chip, Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Password as PasswordIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { api } from '@api/api';
import { ajustarTamanhoFonteAnalista } from "../../../estilos/global";

const InstituicoesList = () => {
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [instituicaoSelecionada, setInstituicaoSelecionada] = useState(null);
  const [emailEditado, setEmailEditado] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [requisitosSenha, setRequisitosSenha] = useState({
    minimoCaracteres: false,
    letraMaiuscula: false,
    numero: false,
    caractereEspecial: false,
    senhasConferem: false
  });
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    buscarInstituicoes();
  }, []);

  const buscarInstituicoes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(api.listMantUsers(), {
        withCredentials: true,
      });
      setInstituicoes(response.data);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar instituições',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const validarSenha = (senha, confirmacao) => {
    const minimoCaracteres = senha.length >= 8;
    const letraMaiuscula = /[A-Z]/.test(senha);
    const numero = /[0-9]/.test(senha);
    const caractereEspecial = /[!@#$%^&*(),.?":{}|<>]/.test(senha);
    const senhasConferem = senha === confirmacao && senha !== '';
    
    setRequisitosSenha({
      minimoCaracteres,
      letraMaiuscula,
      numero,
      caractereEspecial,
      senhasConferem
    });
  };

  const abrirDialogEdicao = (instituicao) => {
    setInstituicaoSelecionada(instituicao);
    setEmailEditado(instituicao.email);
    setNovaSenha('');
    setConfirmarSenha('');
    setEditDialogOpen(true);
    setRequisitosSenha({
      minimoCaracteres: false,
      letraMaiuscula: false,
      numero: false,
      caractereEspecial: false,
      senhasConferem: false
    });
  };

  const confirmarEdicao = async () => {
    if (!instituicaoSelecionada || !instituicaoSelecionada.id) {
      return;
    }

    if (novaSenha && novaSenha !== confirmarSenha) {
      setSnackbar({
        open: true,
        message: 'As senhas não coincidem!',
        severity: 'error'
      });
      return;
    }

    if (novaSenha && !(requisitosSenha.minimoCaracteres && requisitosSenha.letraMaiuscula && 
                       requisitosSenha.numero && requisitosSenha.caractereEspecial)) {
      setSnackbar({
        open: true,
        message: 'A senha não atende a todos os requisitos de segurança!',
        severity: 'error'
      });
      return;
    }

    try {
      const dadosAtualizacao = {
        email: emailEditado
      };

      if (novaSenha) {
        dadosAtualizacao.senha = novaSenha;
      }

      await axios.patch(
        api.editInfoMant(instituicaoSelecionada.id),
        dadosAtualizacao,
        { withCredentials: true }
      );

      setEditDialogOpen(false);
      setInstituicaoSelecionada(null);
      buscarInstituicoes();
      
      setSnackbar({
        open: true,
        message: 'Instituição atualizada com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error("Erro ao atualizar instituição:", error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar instituição',
        severity: 'error'
      });
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setInstituicaoSelecionada(null);
  };

  const handleSenhaChange = (e) => {
    setNovaSenha(e.target.value);
    validarSenha(e.target.value, confirmarSenha);
  };

  const handleConfirmarSenhaChange = (e) => {
    setConfirmarSenha(e.target.value);
    validarSenha(novaSenha, e.target.value);
  };

  const toggleMostrarSenha = () => {
    setMostrarSenha(!mostrarSenha);
  };

  const toggleMostrarConfirmarSenha = () => {
    setMostrarConfirmarSenha(!mostrarConfirmarSenha);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const instituicoesFiltradas = instituicoes.filter(instituicao => {
    if (termoPesquisa) {
      const termo = termoPesquisa.toLowerCase();
      return (
        instituicao.nome.toLowerCase().includes(termo) ||
        instituicao.email.toLowerCase().includes(termo)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Gerenciamento de Instituições
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 3 }}>
          Gerencie as informações das instituições cadastradas no sistema
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Pesquisar instituições..."
          value={termoPesquisa}
          onChange={(e) => setTermoPesquisa(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '4px',
              backgroundColor: 'background.paper',
            }
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Instituição</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {instituicoesFiltradas.length > 0 ? (
              instituicoesFiltradas.map((instituicao) => (
                <TableRow 
                  key={instituicao.id} 
                  hover
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Box 
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          fontWeight: 'bold'
                        }}
                      >
                        {instituicao.nome.charAt(0).toUpperCase()}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="500">
                          {instituicao.nome}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <EmailIcon color="action" sx={{ mr: 1 }} />
                      {instituicao.email}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      icon={<VerifiedIcon />} 
                      label="Ativo" 
                      color="success" 
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title="Editar instituição" arrow>
                        <IconButton
                          color="primary"
                          onClick={() => abrirDialogEdicao(instituicao)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <Box textAlign="center">
                    <Box 
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        mx: 'auto'
                      }}
                    >
                      <SearchIcon fontSize="large" color="disabled" />
                    </Box>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Nenhuma instituição encontrada
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {termoPesquisa ? 'Tente ajustar sua pesquisa' : 'Adicione uma nova instituição para começar'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de Edição de Instituição */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          py: 2
        }}>
          <EditIcon sx={{ mr: 2 }} />
          Editar Instituição
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Box 
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: ajustarTamanhoFonteAnalista('1.5rem')
              }}
            >
              {instituicaoSelecionada?.nome?.charAt(0).toUpperCase()}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="500">
                {instituicaoSelecionada?.nome}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            value={emailEditado}
            onChange={(e) => setEmailEditado(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: '500' }}>
            <LockIcon sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'middle' }} />
            Alterar Senha
          </Typography>
          <DialogContentText sx={{ mb: 2, color: 'text.secondary' }}>
            Deixe em branco para manter a senha atual
          </DialogContentText>

          <TextField
            fullWidth
            label="Nova Senha"
            type={mostrarSenha ? "text" : "password"}
            variant="outlined"
            value={novaSenha}
            onChange={handleSenhaChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleMostrarSenha}
                    edge="end"
                    size="small"
                  >
                    {mostrarSenha ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Confirmar Senha"
            type={mostrarConfirmarSenha ? "text" : "password"}
            variant="outlined"
            value={confirmarSenha}
            onChange={handleConfirmarSenhaChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleMostrarConfirmarSenha}
                    edge="end"
                    size="small"
                  >
                    {mostrarConfirmarSenha ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {novaSenha && (
            <Box 
              mt={3} 
              p={2} 
              sx={{ 
                backgroundColor: 'action.hover',
                borderRadius: '4px',
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon color="info" sx={{ mr: 1, fontSize: '1rem' }} />
                Requisitos da senha:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {requisitosSenha.minimoCaracteres ? 
                      <CheckIcon color="success" fontSize="small" /> : 
                      <CloseIcon color="error" fontSize="small" />}
                  </ListItemIcon>
                  <Typography variant="body2" color={requisitosSenha.minimoCaracteres ? 'text.primary' : 'text.secondary'}>
                    Mínimo de 8 caracteres
                  </Typography>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {requisitosSenha.letraMaiuscula ? 
                      <CheckIcon color="success" fontSize="small" /> : 
                      <CloseIcon color="error" fontSize="small" />}
                  </ListItemIcon>
                  <Typography variant="body2" color={requisitosSenha.letraMaiuscula ? 'text.primary' : 'text.secondary'}>
                    Pelo menos 1 letra maiúscula
                  </Typography>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {requisitosSenha.numero ? 
                      <CheckIcon color="success" fontSize="small" /> : 
                      <CloseIcon color="error" fontSize="small" />}
                  </ListItemIcon>
                  <Typography variant="body2" color={requisitosSenha.numero ? 'text.primary' : 'text.secondary'}>
                    Pelo menos 1 número
                  </Typography>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {requisitosSenha.caractereEspecial ? 
                      <CheckIcon color="success" fontSize="small" /> : 
                      <CloseIcon color="error" fontSize="small" />}
                  </ListItemIcon>
                  <Typography variant="body2" color={requisitosSenha.caractereEspecial ? 'text.primary' : 'text.secondary'}>
                    Pelo menos 1 caractere especial (!@#$%^&*)
                  </Typography>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {requisitosSenha.senhasConferem ? 
                      <CheckIcon color="success" fontSize="small" /> : 
                      <CloseIcon color="error" fontSize="small" />}
                  </ListItemIcon>
                  <Typography variant="body2" color={requisitosSenha.senhasConferem ? 'text.primary' : 'text.secondary'}>
                    As senhas coincidem
                  </Typography>
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ borderRadius: '4px' }}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={confirmarEdicao}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{ borderRadius: '4px' }}
            disabled={novaSenha && !(requisitosSenha.minimoCaracteres && requisitosSenha.letraMaiuscula && 
                                   requisitosSenha.numero && requisitosSenha.caractereEspecial && 
                                   requisitosSenha.senhasConferem)}
          >
            Salvar Alterações
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InstituicoesList;
